// =============================================================================
// MeuDriver — Hook: useInstructorGallery (Batch S2)
// Galeria de fotos do instrutor — upload, listagem, deleção, reordenação
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GalleryPhoto {
  id: string
  instructor_id: string
  storage_path: string
  caption: string | null
  display_order: number
  created_at: string
  /** URL pública gerada pelo Supabase Storage (populada no hook, não no banco) */
  public_url?: string
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const galleryKeys = {
  all:      ['instructor-gallery'] as const,
  byId:     (instructorId: string) => ['instructor-gallery', instructorId] as const,
}

// Gera URL pública para um storage_path do bucket instructor-gallery
function getPublicUrl(storagePath: string): string {
  const { data } = supabase.storage
    .from('instructor-gallery')
    .getPublicUrl(storagePath.replace('instructor-gallery/', ''))
  return data.publicUrl
}

// ─── useInstructorGallery ─────────────────────────────────────────────────────

export function useInstructorGallery(instructorId: string) {
  return useQuery<GalleryPhoto[]>({
    queryKey: galleryKeys.byId(instructorId),
    enabled:  Boolean(instructorId),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructor_gallery')
        .select('*')
        .eq('instructor_id', instructorId)
        .order('display_order', { ascending: true })

      if (error) throw new Error(error.message)

      return ((data ?? []) as GalleryPhoto[]).map((photo) => ({
        ...photo,
        public_url: getPublicUrl(photo.storage_path),
      }))
    },
  })
}

// ─── useAddGalleryPhoto ───────────────────────────────────────────────────────

interface AddPhotoParams {
  /** Arquivo selecionado pelo usuário */
  file: File
  caption?: string
  /** ID do instrutor (para invalidar o cache correto) */
  instructorId: string
}

export function useAddGalleryPhoto() {
  const queryClient = useQueryClient()

  return useMutation<string, Error, AddPhotoParams>({
    mutationFn: async ({ file, caption, instructorId }) => {
      // 1. Faz upload para o Storage
      const ext       = file.name.split('.').pop() ?? 'jpg'
      const fileName  = `${instructorId}/${Date.now()}.${ext}`
      const storagePath = `instructor-gallery/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('instructor-gallery')
        .upload(fileName, file, { upsert: false, contentType: file.type })

      if (uploadError) throw new Error(uploadError.message)

      // 2. Registra no banco via RPC
      const { data, error } = await supabase.rpc('add_gallery_photo', {
        p_storage_path: storagePath,
        p_caption:      caption ?? null,
      })

      if (error) throw new Error(error.message)
      return data as string
    },
    onSuccess: (_data, { instructorId }) => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.byId(instructorId) })
    },
  })
}

// ─── useDeleteGalleryPhoto ────────────────────────────────────────────────────

interface DeletePhotoParams {
  photoId: string
  storagePath: string
  instructorId: string
}

export function useDeleteGalleryPhoto() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeletePhotoParams>({
    mutationFn: async ({ photoId, storagePath }) => {
      // 1. Remove do banco
      const { error } = await supabase.rpc('delete_gallery_photo', {
        p_photo_id: photoId,
      })
      if (error) throw new Error(error.message)

      // 2. Remove do Storage (best-effort — não falha se arquivo não existir)
      const fileName = storagePath.replace('instructor-gallery/', '')
      await supabase.storage.from('instructor-gallery').remove([fileName])
    },
    onSuccess: (_data, { instructorId }) => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.byId(instructorId) })
    },
  })
}

// ─── useReorderGallery ────────────────────────────────────────────────────────

interface ReorderParams {
  photoIds: string[]
  instructorId: string
}

export function useReorderGallery() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, ReorderParams>({
    mutationFn: async ({ photoIds }) => {
      const { error } = await supabase.rpc('reorder_gallery', {
        p_photo_ids: photoIds,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, { instructorId }) => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.byId(instructorId) })
    },
  })
}
